import { useTu } from "@src/appV2/i18n/useTu";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import {
  Box,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { InfoTooltip } from "@src/appV2/redesign/components/InfoTooltip";
import { Text } from "@src/appV2/redesign/components/Text";
import { ALL_NOTIFICATIONS_LABEL, HOME_HEALTH_ALERT_ROWS, MASTER_ALERT_KEY } from "./constants";
import { type AlertChannel, type HomeHealthAlertEnabledMap, type SavingTarget } from "./types";

interface HomeHealthNotificationPreferencesViewProps {
  emailEnabledByKey: HomeHealthAlertEnabledMap;
  smsEnabledByKey: HomeHealthAlertEnabledMap;
  masterEmailEnabled: boolean;
  masterSmsEnabled: boolean;
  savingTarget: SavingTarget | undefined;
  isLoading: boolean;
  onToggleRow: (channel: AlertChannel, alertKey: string, enabled: boolean) => void;
  onToggleAll: (channel: AlertChannel, enabled: boolean) => void;
}

const COLUMN_WIDTH = 96;

function SmsPlaceholder() {
  return (
    <Text
      inline
      variant="caption"
      sx={(theme) => ({ color: theme.palette.text.secondary })}
      data-testid="notification-blank"
    >
      —
    </Text>
  );
}

interface AlertToggleCellProps {
  ariaLabel: string;
  checked: boolean;
  isSaving: boolean;
  isDisabled: boolean;
  showStaticOff: boolean;
  onChange: (enabled: boolean) => void;
}

function AlertToggleCell(props: AlertToggleCellProps) {
  const { tu } = useTu("facilityUser");

  const { ariaLabel, checked, isSaving, isDisabled, showStaticOff, onChange } = props;

  if (showStaticOff) {
    return (
      <Text inline variant="caption" data-testid="uneditable-notification-option">
        {tu("Off")}
      </Text>
    );
  }

  if (isSaving) {
    return <CircularProgress size={20} />;
  }

  return (
    <Switch
      aria-label={ariaLabel}
      checked={checked}
      disabled={isDisabled}
      size="small"
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

export function HomeHealthNotificationPreferencesView(
  props: HomeHealthNotificationPreferencesViewProps
) {
  const { tu } = useTu("facilityUser");

  const {
    emailEnabledByKey,
    smsEnabledByKey,
    masterEmailEnabled,
    masterSmsEnabled,
    savingTarget,
    isLoading,
    onToggleRow,
    onToggleAll,
  } = props;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const isAnyRowSaving = savingTarget !== undefined;
  const isSavingTarget = (channel: AlertChannel, alertKey: string) =>
    savingTarget?.channel === channel && savingTarget.alertKey === alertKey;

  return (
    <TableContainer>
      <Table size="small" aria-label={tu("Home health notification preferences")}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Text inline semibold variant="caption">
                {tu("Notification")}
              </Text>
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <Text inline semibold variant="caption">
                {tu("Email")}
              </Text>
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <Text inline semibold variant="caption">
                SMS
              </Text>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow
            sx={(theme) => ({
              backgroundColor: theme.palette.background?.muted,
            })}
          >
            <TableCell>
              <Text inline bold variant="body2">
                {ALL_NOTIFICATIONS_LABEL}
              </Text>
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <AlertToggleCell
                ariaLabel={ALL_NOTIFICATIONS_LABEL}
                checked={masterEmailEnabled}
                isDisabled={isAnyRowSaving && !isSavingTarget("EMAIL", MASTER_ALERT_KEY)}
                isSaving={isSavingTarget("EMAIL", MASTER_ALERT_KEY)}
                showStaticOff={false}
                onChange={(enabled) => onToggleAll("EMAIL", enabled)}
              />
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <AlertToggleCell
                ariaLabel={`SMS ${ALL_NOTIFICATIONS_LABEL}`}
                checked={masterSmsEnabled}
                isDisabled={isAnyRowSaving && !isSavingTarget("SMS", MASTER_ALERT_KEY)}
                isSaving={isSavingTarget("SMS", MASTER_ALERT_KEY)}
                showStaticOff={false}
                onChange={(enabled) => onToggleAll("SMS", enabled)}
              />
            </TableCell>
          </TableRow>

          {HOME_HEALTH_ALERT_ROWS.map((row) => {
            const { smsAlertKey } = row;
            const isThisEmailRowSaving = isSavingTarget("EMAIL", row.alertKey);
            const isThisSmsRowSaving = smsAlertKey ? isSavingTarget("SMS", smsAlertKey) : false;
            return (
              <TableRow key={row.alertKey}>
                <TableCell>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Text inline variant="body2">
                      {row.label}
                    </Text>
                    <InfoTooltip title={row.tooltip}>
                      <InfoOutlined
                        aria-label={`${row.label} info`}
                        sx={(theme) => ({
                          fontSize: 16,
                          color: theme.palette.text.secondary,
                          cursor: "help",
                        })}
                      />
                    </InfoTooltip>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
                  <AlertToggleCell
                    ariaLabel={`Email notification for ${row.label}`}
                    checked={emailEnabledByKey[row.alertKey] ?? true}
                    isDisabled={isAnyRowSaving && !isThisEmailRowSaving}
                    isSaving={isThisEmailRowSaving}
                    showStaticOff={!masterEmailEnabled}
                    onChange={(enabled) => onToggleRow("EMAIL", row.alertKey, enabled)}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
                  {smsAlertKey ? (
                    <AlertToggleCell
                      ariaLabel={`SMS notification for ${row.label}`}
                      checked={smsEnabledByKey[smsAlertKey] ?? true}
                      isDisabled={isAnyRowSaving && !isThisSmsRowSaving}
                      isSaving={isThisSmsRowSaving}
                      showStaticOff={!masterSmsEnabled}
                      onChange={(enabled) => onToggleRow("SMS", smsAlertKey, enabled)}
                    />
                  ) : (
                    <SmsPlaceholder />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
